import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { NavHashLink as HashLink } from "react-router-hash-link";
import {
  ShieldCheck,
  Rocket,
  Users,
  Lightbulb,
  BadgeCheck,
  HeartHandshake,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";

// 🔹 Content Data
const valuesData = [
  {
    icon: <BadgeCheck className="w-6 h-6 text-[#2FAE8F]" />,
    title: "Integrity",
    content: "We are transparent, trustworthy, and accountable.",
    colSpan: "col-span-1",
  },
  {
    icon: <HeartHandshake className="w-6 h-6 text-[#2FAE8F]" />,
    title: "Care",
    content:
      "We listen and respect the needs of our colleagues, our customers, and ourselves.",
    colSpan: "col-span-1",
  },
  {
    icon: <Users className="w-6 h-6 text-[#2FAE8F]" />,
    title: "Diversity",
    content:
      "We know that diverse backgrounds, thoughts, and experiences will lead us to the best results.",
    colSpan: "col-span-1",
  },
  {
    icon: <TrendingUp className="w-6 h-6 text-[#2FAE8F]" />,
    title: "Growth Mindset",
    content:
      "We stretch ourselves and take on challenges to expand our skills and capabilities.",
    colSpan: "col-span-1",
  },
  {
    icon: <Lightbulb className="w-6 h-6 text-[#2FAE8F]" />,
    title: "Innovation",
    content:
      "We are creative, curious, and collaborative and we use new ideas to drive our company forward.",
    colSpan: "md:col-span-2 lg:col-span-2",
  },
];

const coreFocusesList = [
  "Aligning the cyber security function with organizations' business objectives",
  "Developing a cyber security operations strategy to significantly reduce risk",
  "Supporting compliance with legal, statutory, regulatory, and contractual obligations",
  "Establishing highly effective cyber security governance for digital transformation",
  "Positioning cyber security programs as market differentiators and competitive advantages",
  "Improving ROI in the cyber security function of organizations",
  "Shortening sales cycles by enabling quick responses to security due diligence",
];

// 🔹 Animation Variants
const revealVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: { delay: custom * 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  }),
};

const About = () => {
  return (
    <>
      <Helmet>
        <title>
          About Synthera Security | Cloud First Cybersecurity Experts
        </title>

        <meta
          name="description"
          content="Learn about Synthera Security's mission, values, cybersecurity expertise, and cloud-first Zero Trust approach."
        />

        <meta
          name="keywords"
          content="Synthera Security, Cybersecurity, Zero Trust, Managed Security Services, Cloud Security, Security Consulting"
        />

        <meta property="og:title" content="About Synthera Security" />

        <meta
          property="og:description"
          content="Learn about Synthera Security's mission, values, and cybersecurity expertise."
        />
        <meta name="robots" content="index, follow" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/company/about"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/company/about"
        />
      </Helmet>

      <div className="bg-[#0F2F2B] text-white font-sans antialiased overflow-x-hidden">
        {/* ================= HERO ================= */}
        <section className="relative min-h-[80vh] flex items-center justify-center pt-20 overflow-hidden">
          {/* Background Gradients & Patterns */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#2FAE8F]/10 via-transparent to-transparent opacity-50"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-20"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              custom={0}
            >
              <p className="text-[#2FAE8F] font-medium tracking-widest text-sm uppercase mb-6">
                About Synthera Security
              </p>
              <h1 className="font-['Space_Grotesk',sans-serif] text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8 leading-tight">
                We do cyber
                <br />
                <span className="bg-gradient-to-br from-[#2FAE8F] to-[#7EE0C7] bg-clip-text text-transparent">
                  security better
                </span>
              </h1>
              <p className="max-w-2xl mx-auto text-lg md:text-xl text-gray-400 leading-relaxed">
                A collective of cyber security professionals dedicated to
                aligning security initiatives with business goals.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              custom={2}
              className="mt-16 flex justify-center"
            >
              <div className="w-px h-24 bg-gradient-to-b from-[#2FAE8F] to-transparent"></div>
            </motion.div>
          </div>
        </section>

        {/* ================= WHO WE ARE ================= */}
        <section className="py-32 bg-[#E8F3F1] text-[#0F2F2B]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={revealVariants}
              >
                <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-5xl font-bold mb-8 tracking-tight">
                  Who We Are
                </h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    Synthera Security is built on the trust we earn from our
                    clients, and we continue to nurture that trust with every
                    company we work with.
                  </p>
                  <p>
                    We are a collective of cyber security professionals who want
                    to do cyber security consulting better. We work closely with
                    our clients to align cyber security initiatives with their
                    business goals and ensure everything we do enables,
                    protects, advances, and secures the organizations we work
                    with.
                  </p>
                  <p>
                    The DNA of Synthera Security was forged out of the cyber
                    security community by members who wanted to do security
                    better. Synthera's founders realized the potential of their
                    community and resolved to develop a collective with a great
                    culture.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={revealVariants}
                className="relative"
              >
                <div className="aspect-square bg-[#E8F3F1]  rounded-3xl p-8 flex items-center justify-center relative z-10">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-[#0F2F2B] rounded-full mx-auto mb-6 flex items-center justify-center">
                      <ShieldCheck className="w-16 h-16 text-[#2FAE8F]" />
                    </div>
                    <p className="font-['Space_Grotesk',sans-serif] text-2xl font-semibold">
                      Trust. Excellence. Security.
                    </p>
                  </div>
                </div>
                {/* Blur decoration */}
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#2FAE8F]/10 rounded-full blur-3xl"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ================= MISSION ================= */}
        <section className="py-32 bg-[#0F2F2B] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-[#2FAE8F]/5 via-transparent to-transparent"></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={revealVariants}
            >
              <p className="text-[#2FAE8F] font-medium tracking-widest text-sm uppercase mb-6">
                Our Mission
              </p>
              <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-6xl font-bold mb-8 leading-tight">
                Provide cutting-edge cyber security
                <br />
                services and solutions
              </h2>
              <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
                While growing a community of information security experts who
                want to do security better.
              </p>
              <HashLink
                smooth
                to="/Services"
                className="inline-flex items-center gap-2 bg-white text-[#0F2F2B] px-8 py-4 rounded-full font-medium hover:bg-gray-200 transition-all hover:scale-105"
              >
                View Services
                <ArrowRight className="w-4 h-4" />
              </HashLink>
            </motion.div>
          </div>
        </section>

        {/* ================= VALUES ================= */}
        <section className="py-32 bg-[#123833]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={revealVariants}
              className="text-center mb-20"
            >
              <p className="text-[#2FAE8F] font-medium tracking-widest text-sm uppercase mb-4">
                Our Values
              </p>
              <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-5xl font-bold">
                What drives us forward
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {valuesData.map((v, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUpVariants}
                  custom={i}
                  className={`group p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all duration-400 ${v.colSpan}`}
                >
                  <div className="w-12 h-12 bg-[#2FAE8F]/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    {v.icon}
                  </div>
                  <h3 className="font-['Space_Grotesk',sans-serif] text-xl font-semibold mb-3 text-white">
                    {v.title}
                  </h3>
                  <p className="text-gray-400 max-w-xl">{v.content}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= CORE FOCUSES ================= */}
        <section className="py-32 bg-[#E8F3F1] text-[#0F2F2B]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={revealVariants}
              >
                <p className="text-[#2FAE8F] font-medium tracking-widest text-sm uppercase mb-4">
                  Core Focuses
                </p>
                <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-5xl font-bold mb-8">
                  Strategic priorities that define our approach
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We focus on delivering measurable outcomes that align security
                  with business success, ensuring every initiative drives value.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={revealVariants}
                className="space-y-4"
              >
                {coreFocusesList.map((item, i) => (
                  <div
                    key={i}
                    className="relative overflow-hidden p-6 rounded-xl hover:bg-white transition-all duration-300 group shadow-sm hover:shadow-md cursor-default pl-6 hover:pl-8 before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-[#2FAE8F] before:scale-y-0 hover:before:scale-y-100 before:transition-transform before:duration-400 before:origin-center"
                  >
                    <p className="font-medium text-lg text-gray-800">{item}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ================= JOIN TEAM CTA ================= */}
        <section className="py-32 bg-[#0F2F2B] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#2FAE8F]/5 to-transparent"></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={revealVariants}
            >
              <div className="w-16 h-16 bg-[#2FAE8F]/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <Rocket className="w-8 h-8 text-[#2FAE8F]" />
              </div>
              <h2 className="font-['Space_Grotesk',sans-serif] text-4xl md:text-5xl font-bold mb-6 text-white">
                Interested in joining the Synthera Security team?
              </h2>
              <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                The DNA of Synthera Security was forged out of the cyber
                security community by members who wanted to do security better.
                The founding members realized the potential of the community
                they were a part of and focused on growing a collective of
                like-minded professionals.
              </p>
              <Link
                to="/company/careers"
                className="inline-flex items-center gap-2 bg-[#2FAE8F] text-white px-8 py-4 rounded-full font-medium hover:bg-[#258d73] transition-all hover:scale-105 shadow-lg hover:shadow-[#2FAE8F]/25"
              >
                View Open Positions
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
